'use client'

import { useEffect, useRef, useState } from 'react'

interface LinkDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (url: string) => void
  onRemove?: () => void
  initialUrl?: string
}

export function LinkDialog({ open, onClose, onConfirm, onRemove, initialUrl = '' }: LinkDialogProps) {
  const [url, setUrl] = useState(initialUrl)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setUrl(initialUrl)
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open, initialUrl])

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  const handleConfirm = () => {
    if (url.trim()) {
      onConfirm(url.trim())
    } else {
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-ink/40" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        className="relative z-10 bg-paper border border-ink p-6 w-full max-w-sm font-mono"
      >
        <p className="text-xs font-bold uppercase mb-3">Link einfügen</p>
        <input
          ref={inputRef}
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleConfirm() }}
          placeholder="https://"
          className="w-full border border-ink px-3 py-2 text-sm bg-paper text-ink font-mono mb-4"
        />
        <div className="flex gap-2 justify-end">
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="px-4 py-2 text-xs font-bold uppercase font-mono border border-ink text-ink hover:bg-ink hover:text-paper transition-colors cursor-pointer mr-auto"
            >
              Entfernen
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold uppercase font-mono border border-ink text-ink hover:bg-ink hover:text-paper transition-colors cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="px-4 py-2 text-xs font-bold uppercase font-mono bg-ink text-paper hover:bg-ink/80 transition-colors cursor-pointer"
          >
            Einfügen
          </button>
        </div>
      </div>
    </div>
  )
}
